import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { landlordAPI } from '../../lib/api';
import { Loader2, LogOut, AlertTriangle } from 'lucide-react';

const REASON_OPTIONS = [
  { value: 'lease_ended', label: 'Lease ended naturally' },
  { value: 'tenant_request', label: 'Tenant requested move-out' },
  { value: 'eviction', label: 'Eviction' },
  { value: 'non_payment', label: 'Non-payment of rent' },
  { value: 'other', label: 'Other (specify)' },
];

const DEPOSIT_OPTIONS = [
  { value: 'none', label: 'No deposit on file' },
  { value: 'returned_full', label: 'Returned in full' },
  { value: 'returned_partial', label: 'Partially returned' },
  { value: 'forfeited', label: 'Forfeited' },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function TenantOffboardDialog({ open, onOpenChange, tenant, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    move_out_date: today(),
    reason: 'lease_ended',
    reason_other: '',
    deposit_handling: 'none',
    deposit_amount_returned: '',
    deposit_amount_forfeited: '',
    deposit_reason: '',
    forwarding_address: '',
    notes: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!tenant?.user_id) return;
    if (form.reason === 'other' && !form.reason_other.trim()) {
      toast.error('Please describe the reason');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        move_out_date: form.move_out_date,
        reason: form.reason,
        reason_other: form.reason === 'other' ? form.reason_other : null,
        deposit_handling: form.deposit_handling,
        deposit_amount_returned: parseFloat(form.deposit_amount_returned) || 0,
        deposit_amount_forfeited: parseFloat(form.deposit_amount_forfeited) || 0,
        deposit_reason: form.deposit_reason || null,
        forwarding_address: form.forwarding_address || null,
        notes: form.notes || null,
      };
      const { data } = await landlordAPI.offboardTenant(tenant.user_id, payload);
      toast.success(
        data.rating_email_sent
          ? 'Tenant offboarded — rating request email sent ✓'
          : 'Tenant offboarded (email delivery failed, but record saved)'
      );
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to offboard tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const showDepositAmounts = ['returned_partial', 'returned_full'].includes(form.deposit_handling);
  const showDepositForfeit = ['returned_partial', 'forfeited'].includes(form.deposit_handling);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="offboard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="w-5 h-5 text-[#C85A42]" />
            Offboard tenant
          </DialogTitle>
          <DialogDescription>
            Close out <strong>{tenant?.name}</strong>'s tenancy. We'll terminate the active lease,
            free up the unit, save a permanent history record, send a final rating request, and then
            deactivate the account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="move_out_date">Move-out date</Label>
            <Input
              id="move_out_date"
              type="date"
              value={form.move_out_date}
              onChange={(e) => update('move_out_date', e.target.value)}
              data-testid="move-out-date"
            />
          </div>

          <div>
            <Label>Reason</Label>
            <Select value={form.reason} onValueChange={(v) => update('reason', v)}>
              <SelectTrigger data-testid="offboard-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.reason === 'other' && (
            <div>
              <Label htmlFor="reason_other">Specify reason</Label>
              <Input
                id="reason_other"
                value={form.reason_other}
                onChange={(e) => update('reason_other', e.target.value)}
                placeholder="e.g. Property sold"
                data-testid="reason-other"
              />
            </div>
          )}

          <Separator />

          <div>
            <Label>Deposit handling</Label>
            <Select value={form.deposit_handling} onValueChange={(v) => update('deposit_handling', v)}>
              <SelectTrigger data-testid="deposit-handling"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEPOSIT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showDepositAmounts && (
            <div>
              <Label htmlFor="dep_returned">Amount returned</Label>
              <Input
                id="dep_returned"
                type="number"
                min="0"
                step="0.01"
                value={form.deposit_amount_returned}
                onChange={(e) => update('deposit_amount_returned', e.target.value)}
                placeholder="0.00"
                data-testid="deposit-returned"
              />
            </div>
          )}

          {showDepositForfeit && (
            <>
              <div>
                <Label htmlFor="dep_forfeited">Amount forfeited</Label>
                <Input
                  id="dep_forfeited"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.deposit_amount_forfeited}
                  onChange={(e) => update('deposit_amount_forfeited', e.target.value)}
                  placeholder="0.00"
                  data-testid="deposit-forfeited"
                />
              </div>
              <div>
                <Label htmlFor="dep_reason">Reason for deduction</Label>
                <Input
                  id="dep_reason"
                  value={form.deposit_reason}
                  onChange={(e) => update('deposit_reason', e.target.value)}
                  placeholder="e.g. Wall damage, unpaid utilities"
                  data-testid="deposit-reason"
                />
              </div>
            </>
          )}

          <Separator />

          <div>
            <Label htmlFor="forwarding_address">Forwarding address (optional)</Label>
            <Input
              id="forwarding_address"
              value={form.forwarding_address}
              onChange={(e) => update('forwarding_address', e.target.value)}
              placeholder="For mail, deposit refund, etc."
              data-testid="forwarding-address"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              placeholder="Condition of unit, handover details, etc."
              rows={3}
              data-testid="offboard-notes"
            />
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 flex gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-amber-800 dark:text-amber-200">
              <p className="font-medium">This action is final</p>
              <p className="text-amber-700 dark:text-amber-300 text-xs mt-0.5">
                The tenant's account will be deactivated after the rating email is sent. They will
                no longer be able to log in. All historical data is preserved in Tenant History.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="cancel-offboard">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#C85A42] hover:bg-[#B04A35]"
            data-testid="confirm-offboard"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Offboard tenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
